import type { ComponentType, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

export type KindRoute = {
  kind: string;
  page: ComponentType;
};

/**
 * Wraps the framework fallback so non-registered kinds still get an NS
 * shell (header, breadcrumb, layout). Receives the framework's original
 * entity-page React element as `children`.
 */
export type FallbackWrapper = ComponentType<{ children: ReactNode }>;

/**
 * Internal Switch factory: builds the kind-routing component used by
 * `createCatalogEntityKindOverride`. Exported for direct unit testing so
 * the test-rig does not need to boot the full NFS test app.
 *
 * Match semantics: case-insensitive, first match in the routes array wins.
 * When no route matches and `Fallback` is provided, the framework element
 * is rendered as children of `Fallback`. Without `Fallback`, the framework
 * element renders raw (legacy behavior).
 */
export function __testOnly_CatalogEntityKindSwitch(
  routes: KindRoute[],
  Fallback?: FallbackWrapper,
) {
  return function CatalogEntityKindSwitch({
    fallback,
  }: {
    fallback: ReactNode;
  }) {
    const { kind } = useParams();
    const match = kind
      ? routes.find(r => r.kind.toLowerCase() === kind.toLowerCase())
      : undefined;
    if (match) {
      const Page = match.page;
      return <Page />;
    }
    if (Fallback) {
      return <Fallback>{fallback}</Fallback>;
    }
    return <>{fallback}</>;
  };
}
